import * as React from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import { DocumentTitle } from '@helpcenter/document-title';
import * as fonts from '@helpcenter/styles/src/fonts';
import { ThemedLink } from '@helpcenter/themed-link';
import { PaperContent } from 'view/layout/paper';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import Heading from '@atlaskit/heading';
import { Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

interface Props {
    title: string;
    icon?: React.ComponentClass | React.FunctionComponent;
    header?: string;
    description?: string;
    callToActionHref?: string;
    callToActionText?: string;
    dataTestId?: string;
    actionHrefTarget?: '_blank' | '_self' | '_parent' | '_top';
}

const SimpleIconPage = ({
    actionHrefTarget,
    title,
    header,
    description,
    callToActionHref,
    callToActionText,
    dataTestId,
    icon: Icon,
}: Props) => {
    const useV2Style = FeatureGates.checkGate('help_center_single_icon_page_style_v2');
    const linkWithUnderlineEnabled = FeatureGates.checkGate('help_center_links_with_underline_for_distinction');

    if (useV2Style) {
        return (
            <DocumentTitle title={title}>
                <PaperContent>
                    <ContainerV2 data-testId={dataTestId}>
                        {!!Icon && <Icon />}
                        {!!header && (
                            <Heading as="h1" size="medium">
                                {header}
                            </Heading>
                        )}
                        {!!description && <Text>{description}</Text>}
                        {!!callToActionHref && !linkWithUnderlineEnabled && (
                            <Link to={callToActionHref} actionSubjectId="callToAction" target={actionHrefTarget}>
                                {callToActionText}
                            </Link>
                        )}
                        {!!callToActionHref && linkWithUnderlineEnabled && (
                            <LinkWithUnderline
                                to={callToActionHref}
                                actionSubjectId="callToAction"
                                target={actionHrefTarget}
                            >
                                {callToActionText}
                            </LinkWithUnderline>
                        )}
                    </ContainerV2>
                </PaperContent>
            </DocumentTitle>
        );
    }
    return (
        <DocumentTitle title={title}>
            <PaperContent>
                <Container data-testId={dataTestId}>
                    {!!Icon && <Icon />}
                    {!!header && <Message>{header}</Message>}
                    {!!description && <Description>{description}</Description>}
                    {!!callToActionHref && !linkWithUnderlineEnabled && (
                        <Link to={callToActionHref} actionSubjectId="callToAction" target={actionHrefTarget}>
                            {callToActionText}
                        </Link>
                    )}
                    {!!callToActionHref && linkWithUnderlineEnabled && (
                        <LinkWithUnderline
                            to={callToActionHref}
                            actionSubjectId="callToAction"
                            target={actionHrefTarget}
                        >
                            {callToActionText}
                        </LinkWithUnderline>
                    )}
                </Container>
            </PaperContent>
        </DocumentTitle>
    );
};

export default SimpleIconPage;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Message = styled.div`
    ${fonts.h600};
    /* setting max-width 100% because on IE, text flows out of the container */
    max-width: 100%;
    margin: 0 0 ${token('space.100', '8px')} 0;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Description = styled.p`
    ${fonts.regular};
    /* setting max-width 100% because on IE, text flows out of the container */
    max-width: 100%;
    margin: 0 0 ${token('space.100', '8px')} 0;
    text-align: center;
`;

// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const Container = styled.div`
    display: flex;
    flex-flow: column wrap;
    align-items: center;
    margin-top: ${token('space.600', '48px')};
`;

// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const ContainerV2 = styled.div({
    display: 'flex',
    flexFlow: 'column wrap',
    alignItems: 'center',
    gap: token('space.100', '8px'),
    marginTop: token('space.600', '48px'),
});

// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const Link = styled(ThemedLink)`
    text-align: center;
`;

// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const LinkWithUnderline = styled(ThemedLink)`
    text-align: center;
    text-decoration: underline;
`;
